import CalendarMonth from "@mui/icons-material/CalendarMonth";
import { Box, Button, Tooltip } from "@mui/material";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchCommandButtonsContext } from "components/OmnisearchDataDisplay";
import { TasksDisplay } from "components/Tasks/TasksDisplay";
import { Permission, TasksQuery } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { AddTaskDialog } from "./AddTaskDialog";
import { getDefaultTaskSearch } from "./defaultSearch";
import { SuggestedTasksGrid } from "./SuggestedTasks";
import { TaskCalendarSubscriptionDialog } from "./TaskCalendarSubscriptionDialog";
import { TaskFilterChips, TaskFilterType } from "./TaskFilterChips";
import { useTaskFilterChips } from "./useTaskFilterChips";

export type Row = TasksQuery["virtualEvents"]["items"][0];

export const Tasks = () => {
  const { tenantId } = useParams();

  // Suggested Tasks Disabled: keep code, but prevent UI + queries from running.
  const isSuggestedTasksEnabled = false;

  const { activeFilter } = useTaskFilterChips();

  const { hasPermissions } = useAuthorization();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openCalendarSubscription, setOpenCalendarSubscription] = useState(false);

  const { user } = useCurrentUser();
  const canAddTask = useMemo(() => {
    return hasPermissions([Permission.WriteAllFacility]) || (user?.facilities.length ?? 0) > 0;
  }, [hasPermissions, user?.facilities.length]);

  useBreadcrumb([{ label: "Tasks" }]);

  const additionFilterColumns: FilterKey[] = [
    {
      key: "facility",
      header: "Facility Name",
      filterKeyType: "facility",
    },
    {
      key: "facilityTags",
      header: "Facility Tags",
      filterKeyType: "facilityTags",
    },
  ];

  const commandButtons: (context: OmnisearchCommandButtonsContext) => React.ReactNode[] =
    useCallback(
      ({ search }) => {
        const buttons = [
          <ExportDataButton
            key="export-tasks-button"
            tenantId={tenantId}
            exportType="TASKS"
            filter={{ search }}
            title="Export Tasks"
            fileName="tasks-export"
          />,
          <Tooltip
            title="Subscribe to tasks in your calendar app"
            key="calendar-subscription-tooltip"
          >
            <Button
              key="calendar-subscription-button"
              variant="outlined"
              startIcon={<CalendarMonth />}
              onClick={() => setOpenCalendarSubscription(true)}
            >
              Subscribe
            </Button>
          </Tooltip>,
        ];

        // Add task button for users with permission
        if (canAddTask) {
          buttons.push(
            <Button key="add-task-button" variant="contained" onClick={() => setOpenAddModal(true)}>
              Add Task
            </Button>
          );
        }

        return buttons;
      },
      [tenantId, canAddTask]
    );

  // Determine what to show based on filter
  const showSuggestedOnly = activeFilter === TaskFilterType.Suggested && isSuggestedTasksEnabled;

  const defaultSearch = getDefaultTaskSearch(tenantId, activeFilter);
  const defaultCalendarSearch = getDefaultTaskSearch(tenantId, TaskFilterType.AllButCanceled);

  return (
    <>
      <Box sx={{ pt: 3 }}>
        {/* Show only SuggestedTasksGrid when "suggested" filter is active */}
        {showSuggestedOnly ? (
          tenantId && <SuggestedTasksGrid tenantId={tenantId} />
        ) : (
          <TasksDisplay
            additionalFilterColumns={additionFilterColumns}
            commandButtons={commandButtons}
            defaultSearch={defaultSearch}
            defaultCalendarSearch={defaultCalendarSearch}
            topContent={
              <Box sx={{ ml: 3 }}>
                <TaskFilterChips />
              </Box>
            }
          />
        )}
      </Box>
      {openAddModal && <AddTaskDialog open={openAddModal} onClose={() => setOpenAddModal(false)} />}
      {openCalendarSubscription && (
        <TaskCalendarSubscriptionDialog
          open={openCalendarSubscription}
          onClose={() => setOpenCalendarSubscription(false)}
        />
      )}
    </>
  );
};
